import { DataService } from 'api/DataService'

import type {
  ChangelogCategory,
  ChangelogEntry,
  ChangelogKind,
  ChangelogListResponse,
  ChangelogProject,
  ChangelogSuggestionPayload,
  ChangelogSuggestionResponse
} from '../types'

export type ChangelogFilters = {
  kind?: ChangelogKind[]
  projects?: ChangelogProject[]
  category?: ChangelogCategory[]
}

type PaginationMeta = {
  current_page: number
  next_page: number | null
  prev_page: number | null
  total_pages: number
  total_count: number
}

type ApiEnvelope<T> = {
  error: boolean
  message?: string
  record?: T
  records?: T[]
  meta?: PaginationMeta
  detail_error?: unknown
}

const buildFilterQuery = (filters?: ChangelogFilters): string => {
  if (!filters) return ''
  const params: string[] = []
  filters.kind?.forEach((k) => params.push(`kind[]=${encodeURIComponent(k)}`))
  filters.projects?.forEach((p) =>
    params.push(`projects[]=${encodeURIComponent(p)}`)
  )
  filters.category?.forEach((c) =>
    params.push(`category[]=${encodeURIComponent(c)}`)
  )
  return params.length ? `&${params.join('&')}` : ''
}

export const fetchChangelogEntry = async (
  uuid: string
): Promise<ChangelogEntry | null> => {
  const response = await DataService<ApiEnvelope<ChangelogEntry>>({
    type: 'GET',
    url: `/v1/changelog_entries/${uuid}`
  })
  if (response?.error || !response?.data?.record) return null
  return response.data.record
}

export const fetchChangelogPage = async (
  page = 1,
  perPage = 10,
  filters?: ChangelogFilters
): Promise<ChangelogListResponse> => {
  const fallback: ChangelogListResponse = {
    items: [],
    total: 0,
    page,
    per_page: perPage,
    has_more: false
  }
  const response = await DataService<ApiEnvelope<ChangelogEntry>>({
    type: 'GET',
    url: `/v1/changelog_entries?page=${page}&per_page=${perPage}${buildFilterQuery(
      filters
    )}`
  })
  if (response?.error || !response?.data?.records) return fallback

  const { records, meta } = response.data
  return {
    items: records,
    total: meta?.total_count ?? records.length,
    page: meta?.current_page ?? page,
    per_page: perPage,
    has_more: meta?.next_page != null
  }
}

export type ChangelogHomePayload = {
  banner: ChangelogEntry | null
  modal: ChangelogEntry | null
  unread_count: number
}

export const fetchHomePayload = async (): Promise<ChangelogHomePayload> => {
  const fallback: ChangelogHomePayload = {
    banner: null,
    modal: null,
    unread_count: 0
  }
  const response = await DataService<ApiEnvelope<ChangelogHomePayload>>({
    type: 'GET',
    url: '/v1/changelog_entries/home_payload'
  })
  if (response?.error || !response?.data?.record) return fallback
  return response.data.record
}

export const markAllSeen = async () =>
  DataService({ type: 'POST', url: '/v1/changelog_entries/seen' })

export const dismissOneShot = async (uuid: string) =>
  DataService({
    type: 'POST',
    url: `/v1/changelog_entries/${uuid}/dismiss`
  })

export const createSuggestion = async (payload: ChangelogSuggestionPayload) => {
  const { attachments = [], ...rest } = payload
  let data: FormData | ChangelogSuggestionPayload = rest
  if (attachments.length > 0) {
    const fd = new FormData()
    fd.append('content', rest.content)
    attachments.forEach((file) => fd.append('attachments[]', file))
    data = fd
  }
  const response = await DataService<ApiEnvelope<ChangelogSuggestionResponse>>({
    type: 'POST',
    url: '/v1/changelog_suggestions',
    data
  })
  if (response?.error) return { ok: false as const }
  return { ok: true as const, uuid: response.data?.record?.uuid }
}
